import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DependentsStyled } from '../../asset/styled/DependentsStyled';
import Button from '../../components/common/Button';
import { useTranslation } from 'react-i18next';
import { relationHtm002Options, relationOptions } from '../../common/data';
import { SelectBox } from '../../components/common/SelectBox';
import TextField from '../../components/common/TextField';
import { useLocation, useNavigate } from 'react-router-dom';
import { openSlideUp } from '../common/dialogSlice';
import {
  getAge,
  getAlertMessageFunction,
  getCancelDialogConfig,
  getCurrentYear,
  magamCertifyCheck,
  setRegNumber,
} from '../../common/Util';
import {
  getDependentsDetailInfo,
  resetDependentsState,
  setSelectedDependents,
  updateDependentsDetailInfo,
} from './dependentsSlice';
import SlideRightDialog from '../../components/common/SlideRightDialog';
import { setIsDataModified } from '../common/commonSlice';
import DetailButtonSection from '../../components/DetailButtonSection';
import { checkFormData } from './util';
import SlideDialogV from '../../components/common/SlideDialogV';

const DependentsAdd = ({ isOpen, onClose, add }) => {
  const { t: tDependents } = useTranslation('dependents');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const relations = relationOptions(tDependents);
  const relationsHtm002 = relationHtm002Options(tDependents);
  const { isMagam, isCertify } = useSelector(state => state.common);
  const { selectedDependents, detailData, dataList } = useSelector(state => state.dependents);
  // 직계비속 or 직계존속 dialog  1: 직계비속, 2: 직계존속
  const [isSlideDialogV, setIsSlideDialogV] = useState(false);

  const [data, setData] = useState({
    htm001_old: '', //기존 주민
    htm001: '', //주민
    htm038: '', //성명
    htm002_old: '', //기존 관계
    htm002: '', //관계
    htm032: '', //내 외국인
    htm036: '', //자녀구분 1:자녀 , 2:손자손녀, 3:사위, 4:며느리
  });

  useEffect(() => {
    if (isOpen) {
      if (selectedDependents?.htm001) {
        console.log('selectedDependents >>', selectedDependents);
        dispatch(getDependentsDetailInfo(selectedDependents));
      }
    }
  }, [dispatch, isOpen, selectedDependents]);

  useEffect(() => {
    if (isOpen && detailData && selectedDependents?.htm001) {
      setData({
        htm001_old: detailData.htm001 || '',
        htm001: detailData.htm001 || '',
        htm038: detailData.htm038 || '',
        htm002_old: detailData.htm002 || '',
        htm002: detailData.htm002 || '',
        htm032: detailData.htm032 || '',
        htm036: detailData.htm036 || '',
      });
    }
  }, [detailData, isOpen]);

  const handleClose = () => {
    dispatch(setSelectedDependents({}));
    setData({
      htm001_old: '', //기존 주민
      htm001: '', //주민
      htm038: '', //성명
      htm002_old: '', //기존 관계
      htm002: '', //관계
      htm032: '', //내 외국인
      htm036: '',
    });
    onClose();
  };

  const handleChange = e => {
    let value = e.target.value;
    if (e.target.name === 'htm001') {
      value = setRegNumber(value);
    }

    dispatch(setIsDataModified(true));

    setData(prevData => ({
      ...prevData,
      [e.target.name]: value || '',
    }));
  };

  const handleSave = async () => {
    if (!magamCertifyCheck(isMagam, isCertify)) {
      return;
    }

    const alertMessage = getAlertMessageFunction();
    try {
      if (checkFormData(data, alertMessage, tDependents, dataList, setIsSlideDialogV)) {
        const { htm002_old, ...restData } = data;
        let formData = [
          {
            ...restData,
            htm001: data.htm001.replace(/-/g, ''),
          },
        ];
        const response = await dispatch(updateDependentsDetailInfo({ formData })).unwrap();
        if (response.resultCode === 'OK') {
          alertMessage(tCommon('alertMessage.saveSuccess'), 'success');
          handleClose();
        } else {
          alertMessage(tCommon('alertMessage.saveFail'), 'error');
        }
      }
    } catch (error) {
      alertMessage(tCommon('alertMessage.saveFail'), 'error');
      console.error('부양가족 정보 업데이트 실패:', error);
    }
  };

  //직계비속, 직계존속 slideDialog 버튼 클릭시 호출
  const handleSaveAPI = async () => {
    const { htm002_old, ...restData } = data;
    let formData = [
      {
        ...restData,
        htm001: data.htm001.replace(/-/g, ''),
      },
    ];
    const alertMessage = getAlertMessageFunction();
    const response = await dispatch(updateDependentsDetailInfo({ formData })).unwrap();
    if (response.resultCode === 'OK') {
      alertMessage(tCommon('alertMessage.saveSuccess'), 'success');
      setIsSlideDialogV(false);
      handleClose();
    } else {
      alertMessage(tCommon('alertMessage.saveFail'), 'error');
    }
  };

  return (
    <SlideRightDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={add ? tDependents('addButton') : null}>
      <DependentsStyled>
        {/* 관계 */}
        <SelectBox
          label={tDependents('add.input.relation')}
          value={data.htm002}
          onChange={handleChange}
          name="htm002">
          {relations.map(
            relation =>
              !relation.isDisabled && (
                <option key={relation.value} value={relation.value}>
                  {relation.label}
                </option>
              ),
          )}
        </SelectBox>
        {data.htm002 === '4' && (
          <SelectBox
            label={tDependents('add.input.relation')}
            value={data.htm036}
            onChange={handleChange}
            name="htm036">
            {relationsHtm002.map(relation => (
              <option key={relation.value} value={relation.value}>
                {relation.label}
              </option>
            ))}
          </SelectBox>
        )}
        {/* 성명 */}
        <TextField
          label={tDependents('add.input.name')}
          placeholder={tDependents('add.input.namePlaceholder')}
          value={data.htm038}
          size="small"
          onChange={handleChange}
          name="htm038"
        />
        {/* 주민등록번호 */}
        <TextField
          label={tDependents('add.input.residentNumber')}
          placeholder={tDependents('add.input.residentNumberPlaceholder')}
          message={tDependents('add.input.residentNumberMessage')}
          value={setRegNumber(data.htm001)}
          size="small"
          messageStatus=""
          onChange={handleChange}
          name="htm001"
        />
        {/* 버튼 */}
        <DetailButtonSection onSave={handleSave} onClose={handleClose} />
        {/* <div className="displayFlex" style={{ gap: '8px' }}>
          <Button value={tCommon('cancel')} color="secondary" onClick={handleOpenCancelDialog} />
          <Button value={tCommon('save')} style={{ flex: 1 }} onClick={handleSave} />
        </div> */}
      </DependentsStyled>
      <SlideDialogV
        isOpen={isSlideDialogV}
        onClose={() => setIsSlideDialogV(false)}
        title={tDependents('slideDialog4.title')}
        description={
          isSlideDialogV == '1'
            ? tDependents('slideDialog4.description1')
            : tDependents('slideDialog4.description2')
        }
        buttons={[
          {
            label: tDependents('slideDialog4.button1'),
            onClick: handleSaveAPI,
          },
          {
            label: tDependents('slideDialog4.button2'),
            onClick: () => setIsSlideDialogV(false),
            color: 'secondary',
          },
        ]}
      />
    </SlideRightDialog>
  );
};

export default DependentsAdd;
